import * as Lib from './library.js';
import { initZoom } from './zoom.js';
import {
    initAuth,
    logout,
    consumePendingDownloadGateIntent,
    consumePostLoginReturnContext,
    peekPendingDownloadGateIntent,
    peekPostLoginReturnContext,
} from './auth.js';

import { attachHistoryScroll, loadHistoryPage, reloadHistoryFirstPage, resetHistoryState } from './history_loader.js';
import * as API from './api.js';
import * as Presets from './presets.js';
import * as JobManager from './job_manager.js';
import { state } from './state.js';
import { handleHistorySelect } from './history_actions.js';
import { setupJobEventHandlers } from './job_event_handlers.js';
import { initTargetHandlers, applyPlanLimits, refreshTargetDropLimitHint } from './target_controller.js';
import { initLibraryUploadHandlers } from './library_upload_handlers.js';
import { initControlHandlers } from './main_controls.js';
import * as ResultViewer from './result_viewer.js';
import { applySliderConfig } from './config/sliders.js';
import { loadAndApplyPolicyConfig } from './config/policy.js';
import { initSettingsMode, refreshLegacyDraftControls } from './settings_mode.js';
import { initHelpPopovers } from './help_popovers.js';
import * as SourcePhotos from './source_photos.js';
import { showToast } from './ui/toast.js';
import { initGuidedOnboarding } from './onboarding.js';
import { initWorkflowCollapsibles, refreshHistoryEmptyState, refreshSettingsVisibility, requestWorkflowScroll } from './workflow_selection.js';
import { initCreateDraftPersistence, restoreCreateDraftState } from './workflow_draft.js';
import {
    enableHorizontalWheelScroll,
    enableProxyWheelScroll,
    enablePageEdgeFeedback,
    enableWorkflowWheelRouting
} from './ui/scroll.js';
import { refreshLibraryDropLimitHint } from './ui/library_workspace.js';

const BOOT_T0_MS = (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now();

function bootNowMs() {
    return (typeof performance !== 'undefined' && typeof performance.now === 'function')
        ? performance.now()
        : Date.now();
}

function getNavigationType() {
    try {
        if (typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function') {
            const entries = performance.getEntriesByType('navigation');
            if (entries && entries.length) {
                const entry = entries[0];
                const type = entry && typeof entry.type === 'string' ? entry.type : '';
                if (type) return type;
            }
        }
        if (typeof performance !== 'undefined' && performance.navigation) {
            const legacy = performance.navigation.type;
            if (legacy === 1) return 'reload';
            if (legacy === 2) return 'back_forward';
            if (legacy === 0) return 'navigate';
        }
    } catch (e) {}
    return '';
}

function logBootFlow(action, outcome, metadata = {}, durationMs = null) {
    if (state.isAnonymous) return;
    API.logUiFlow('boot', action, outcome, metadata, durationMs).catch(() => {});
}

function normalizeRolloutMode(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'refactor') return 'refactor';
    return 'refactor';
}

function trackRefactorRollout(policy) {
    const rollout = (policy && typeof policy === 'object' && policy.refactor_rollout)
        ? policy.refactor_rollout
        : {};
    const billingMode = normalizeRolloutMode(rollout.billing_mode);
    const viewerMode = normalizeRolloutMode(rollout.viewer_mode);
    state.refactorRollout = { billingMode, viewerMode };
    if (state.isAnonymous) return;
    API.logEvent('refactor_rollout_selected', {
        module: 'billing',
        mode: billingMode,
        viewer_mode: viewerMode,
        source: 'app_boot',
    }).catch(() => {});
    API.logEvent('refactor_rollout_selected', {
        module: 'viewer',
        mode: viewerMode,
        billing_mode: billingMode,
        source: 'app_boot',
    }).catch(() => {});
}

function timed(task) {
    const startedAt = bootNowMs();
    try {
        const promise = (typeof task === 'function') ? task() : task;
        return Promise.resolve(promise).then(
            (value) => ({
                status: 'fulfilled',
                value,
                durationMs: bootNowMs() - startedAt,
            }),
            (reason) => ({
                status: 'rejected',
                reason,
                durationMs: bootNowMs() - startedAt,
            }),
        );
    } catch (reason) {
        return Promise.resolve({
            status: 'rejected',
            reason,
            durationMs: bootNowMs() - startedAt,
        });
    }
}

function setHistorySkeletonVisible(visible) {
    const strip = document.getElementById('historyStrip');
    if (!strip) return;
    const show = Boolean(visible);
    const nodes = strip.querySelectorAll('.history-skeleton');
    nodes.forEach((node) => node.classList.toggle('hidden', !show));
}

function bindHistoryRetry() {
    const retryBtn = document.getElementById('historyRetryBtn');
    if (!retryBtn) return;
    retryBtn.addEventListener('click', async () => {
        if (state.historyLoading) return;
        state.historyBootStatus = 'loading';
        setHistorySkeletonVisible(true);
        refreshHistoryEmptyState();
        resetHistoryState();
        try {
            await loadHistoryPage();
        } catch (e) {
            state.historyBootStatus = 'error';
            setHistorySkeletonVisible(false);
            refreshHistoryEmptyState();
        }
    });
}

async function _refreshHistoryUntilJobVisible(targetJobId = '') {
    const safeTargetJobId = String(targetJobId || '').trim();
    const attemptsMax = safeTargetJobId ? 4 : 1;
    for (let attempt = 0; attempt < attemptsMax; attempt += 1) {
        try {
            await reloadHistoryFirstPage();
        } catch (_e) {
            if (attempt >= (attemptsMax - 1)) return false;
        }
        if (!safeTargetJobId) return true;
        const found = (state.history || []).some((job) => String(job.job_id || '') === safeTargetJobId);
        if (found) return true;
        await new Promise((resolve) => window.setTimeout(resolve, 400 + (attempt * 300)));
    }
    return false;
}

function _ensureHistoryStageHydrated() {
    const strip = document.getElementById('historyStrip');
    if (!strip) return;
    strip.scrollLeft = 0;
    const hasStateItems = Array.isArray(state.history) && state.history.length > 0;
    const hasDomItems = Boolean(strip.querySelector('.history-item[data-job-id]'));
    if (hasStateItems && !hasDomItems) {
        UI.renderHistory(state.history, handleHistorySelect, {
            reset: true,
            selectJobId: state.selectedHistoryJobId || null,
        });
        UI.updateHistoryMeta();
        return;
    }
    if (!hasStateItems && !state.historyLoading) {
        _refreshHistoryUntilJobVisible('').catch(() => {});
    }
}

let hasTrackedFirstAppAction = false;

function trackAppView() {
    API.logSiteEvent('app_view', {
        page: 'app',
        cta: 'entry',
    }).catch(() => {});
}

function initFirstAppActionTracking() {
    if (hasTrackedFirstAppAction) return;

    const commit = (action) => {
        if (hasTrackedFirstAppAction) return;
        hasTrackedFirstAppAction = true;
        document.removeEventListener('pointerdown', onPointer, true);
        document.removeEventListener('keydown', onKeydown, true);
        API.logSiteEvent('app_first_action', {
            page: 'app',
            cta: action,
        }).catch(() => {});
    };

    const onPointer = (event) => {
        const target = event && event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('#authGate')) return;
        commit('pointer');
    };

    const onKeydown = (event) => {
        if (!event || event.metaKey || event.ctrlKey || event.altKey) return;
        commit('keyboard');
    };

    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKeydown, true);
}

function _readBillingReturnParam() {
    try {
        const params = new URLSearchParams(window.location.search || '');
        return String(params.get('billing') || '').trim().toLowerCase();
    } catch (_e) {
        return '';
    }
}

function _clearBillingReturnParam() {
    try {
        const current = new URL(window.location.href);
        if (!current.searchParams.has('billing')) return;
        current.searchParams.delete('billing');
        const query = current.searchParams.toString();
        const nextUrl = `${current.pathname}${query ? `?${query}` : ''}${current.hash || ''}`;
        window.history.replaceState({}, document.title, nextUrl);
    } catch (_e) {}
}

function _readStageFromHash(hashValue = '') {
    const safe = String(hashValue || '').trim();
    if (!safe) return '';
    const normalized = safe.startsWith('#') ? safe.slice(1) : safe;
    if (!normalized) return '';
    if (normalized.startsWith('stage=')) {
        return String(normalized.slice('stage='.length) || '').trim().toLowerCase();
    }
    return '';
}

async function restoreViewerSelectionForCurrentStage() {
    const stage = _readStageFromHash(window.location.hash || '');
    if (stage !== 'viewer') return false;
    if (String(state.selectedHistoryJobId || '').trim()) return false;
    if (!Array.isArray(state.history) || state.history.length <= 0) return false;

    const candidateIds = [];
    const pending = peekPendingDownloadGateIntent();
    if (pending && pending.job_id) {
        candidateIds.push(String(pending.job_id || '').trim());
    }
    const postLoginCtx = peekPostLoginReturnContext();
    if (postLoginCtx) {
        candidateIds.push(String(postLoginCtx.selected_history_job_id || '').trim());
        candidateIds.push(String(postLoginCtx.viewer_job_id || '').trim());
    }
    candidateIds.push(String(state.viewerJobId || '').trim());
    candidateIds.push(String(state.jobId || '').trim());

    let match = null;
    for (const candidateId of candidateIds) {
        if (!candidateId) continue;
        match = state.history.find((job) => String(job.job_id || '') === candidateId) || null;
        if (match) break;
    }
    if (!match) {
        match = state.history[0] || null;
    }
    if (!match || !match.job_id) return false;

    try {
        state.selectedHistoryJobId = String(match.job_id || '');
        await handleHistorySelect(match, { pushHistory: false });
        return true;
    } catch (_e) {
        return false;
    }
}

async function resumeBillingCheckoutReturnIfAny() {
    const billingState = _readBillingReturnParam();
    if (!billingState) return false;

    if (billingState !== 'success' && billingState !== 'cancelled') {
        _clearBillingReturnParam();
        return false;
    }

    const context = ResultViewer.getSavedBillingReturnContext();
    const contextKind = String((context && context.kind) || '').toLowerCase();
    if (!context || (contextKind !== 'finalize' && contextKind !== 'subscription')) {
        _clearBillingReturnParam();
        if (billingState === 'cancelled') {
            showToast('Checkout cancelled', 'No payment was taken. You can try again anytime.', 3000);
        } else {
            showToast('Checkout finished', 'Open your export again to continue.', 2800);
        }
        return true;
    }

    const targetJobId = String(context.job_id || '').trim();
    const targetSize = Number(context.long_edge_px || 0);
    const ensureHistoryMatch = async () => {
        let match = (state.history || []).find((job) => String(job.job_id || '') === targetJobId);
        let attempts = 0;
        while (!match && state.historyHasMore && attempts < 12) {
            attempts += 1;
            try {
                await loadHistoryPage();
            } catch (_e) {
                break;
            }
            match = (state.history || []).find((job) => String(job.job_id || '') === targetJobId);
        }
        return match || null;
    };

    const historyMatch = await ensureHistoryMatch();
    if (!historyMatch) {
        ResultViewer.clearSavedBillingReturnContext();
        _clearBillingReturnParam();
        if (billingState === 'cancelled') {
            showToast('Checkout cancelled', 'Could not restore your previous mosaic automatically. Open it from Recent Mosaics.', 3400);
        } else {
            showToast('Checkout finished', 'Could not find that mosaic in recent history. Open it manually to continue.', 3200);
        }
        return true;
    }

    try {
        const billing = await API.getBillingStatus();
        if (billing) {
            state.billingStatus = billing;
            if (billing.entitlements) {
                if (!state.plan) state.plan = {};
                state.plan.entitlements = billing.entitlements;
                if (billing.entitlements.plan_name) state.plan.name = billing.entitlements.plan_name;
            }
        }
    } catch (_e) {}

    try {
        state.selectedHistoryJobId = targetJobId;
        await handleHistorySelect(historyMatch);
    } catch (_e) {}

    let modalOpened = false;
    try {
        modalOpened = await ResultViewer.openDownloadModalWhenReady(targetJobId, { timeoutMs: 5000 });
    } catch (_e) {}

    ResultViewer.clearSavedBillingReturnContext();
    if (billingState === 'cancelled') {
        showToast(
            'Checkout cancelled',
            modalOpened
                ? 'No payment was taken. You are back on the same mosaic download options.'
                : 'No payment was taken. Open Download Mosaic to continue.',
            3600,
        );
    } else {
        if (contextKind === 'finalize' && Number.isFinite(targetSize) && targetSize > 0) {
            showToast(
                'Checkout complete',
                modalOpened
                    ? `Select ${Math.round(targetSize)}px and click Ready to generate.`
                    : `Open ${Math.round(targetSize)}px and click Ready to generate.`,
                3600,
            );
        } else {
            showToast(
                'Checkout complete',
                modalOpened
                    ? 'Download options for this mosaic are open.'
                    : 'Open Download Mosaic to continue.',
                3200,
            );
        }
    }
    _clearBillingReturnParam();
    return true;
}

async function restorePostLoginReturnContextIfAny() {
    if (state.isAnonymous) return false;
    const ctx = consumePostLoginReturnContext();
    if (!ctx) return false;

    const stage = _readStageFromHash(ctx.hash);
    const targetJobId = String(ctx.selected_history_job_id || ctx.viewer_job_id || '').trim();
    const createStep = String(ctx.create_step || '').trim().toLowerCase();
    const selectedSourcePhotoId = String(ctx.selected_source_photo_id || '').trim();
    const selectedLibraryId = String(ctx.selected_library_id || '').trim();

    const ensureHistoryMatch = async () => {
        if (!targetJobId) return null;
        let match = (state.history || []).find((job) => String(job.job_id || '') === targetJobId);
        let attempts = 0;
        while (!match && state.historyHasMore && attempts < 12) {
            attempts += 1;
            try {
                await loadHistoryPage();
            } catch (_e) {
                break;
            }
            match = (state.history || []).find((job) => String(job.job_id || '') === targetJobId);
        }
        return match || null;
    };

    if (targetJobId) {
        const historyMatch = await ensureHistoryMatch();
        if (historyMatch) {
            try {
                state.selectedHistoryJobId = targetJobId;
                await handleHistorySelect(historyMatch);
            } catch (_e) {}
        }
    }

    if (stage === 'create') {
        if (selectedSourcePhotoId) {
            try {
                SourcePhotos.selectSourcePhotoById(selectedSourcePhotoId);
            } catch (_e) {}
        }
        if (selectedLibraryId) {
            const hasLibrary = (state.libraries || []).some(
                (lib) => String((lib && lib.id) || '') === selectedLibraryId,
            );
            if (hasLibrary) {
                try {
                    await Lib.handleLibSelect(selectedLibraryId);
                } catch (_e) {}
            }
        }
        requestWorkflowScroll('create', {
            source: 'post_login_restore',
            pushHistory: false,
            stageAnimate: false,
            stageDurationMs: 1,
            behavior: 'auto',
            durationMs: 0,
        });
        if (createStep === 'main' || createStep === 'library' || createStep === 'settings') {
            const btn = document.querySelector(`.create-step-pill[data-create-step="${createStep}"]`);
            if (btn && typeof btn.click === 'function') {
                window.setTimeout(() => btn.click(), 20);
            }
        }
        return true;
    }

    if (stage === 'viewer' && targetJobId) {
        return true;
    }

    if (stage === 'viewer') {
        requestWorkflowScroll('viewer', {
            source: 'post_login_restore',
            pushHistory: false,
            stageAnimate: false,
            stageDurationMs: 1,
            behavior: 'auto',
            durationMs: 0,
        });
        return true;
    }

    if (stage === 'history') {
        requestWorkflowScroll('history', {
            source: 'post_login_restore',
            pushHistory: false,
            stageAnimate: false,
            stageDurationMs: 1,
            behavior: 'auto',
            durationMs: 0,
        });
        return true;
    }

    return false;
}

async function resumePendingDownloadGateAfterLoginIfAny() {
    if (state.isAnonymous) return false;
    const pending = consumePendingDownloadGateIntent();
    if (!pending) return false;
    const targetJobId = String((pending && pending.job_id) || '').trim();
    if (!targetJobId) return false;

    const ensureHistoryMatch = async () => {
        let match = (state.history || []).find((job) => String(job.job_id || '') === targetJobId);
        let attempts = 0;
        while (!match && state.historyHasMore && attempts < 12) {
            attempts += 1;
            try {
                await loadHistoryPage();
            } catch (_e) {
                break;
            }
            match = (state.history || []).find((job) => String(job.job_id || '') === targetJobId);
        }
        return match || null;
    };

    const historyMatch = await ensureHistoryMatch();
    if (!historyMatch) {
        showToast(
            'Signed in',
            'Your mosaic is in My Mosaics. Open it and click Download Mosaic.',
            3200,
        );
        return true;
    }

    try {
        state.selectedHistoryJobId = targetJobId;
        await handleHistorySelect(historyMatch);
    } catch (_e) {}

    let modalOpened = false;
    try {
        modalOpened = await ResultViewer.openDownloadModalWhenReady(targetJobId, { timeoutMs: 5000 });
    } catch (_e) {}

    showToast(
        'Signed in',
        modalOpened
            ? 'Download options are ready.'
            : 'Open Download Mosaic to continue.',
        2800,
    );
    return true;
}

async function restoreCompletedTrackedJobIfAny() {
    const trackedJobId = JobManager.getPendingJobId();
    if (!trackedJobId) return false;

    try {
        const status = await API.getStatus(trackedJobId);
        const phase = String((status && status.phase) || '').toLowerCase();

        if (phase === 'complete') {
            const match = (state.history || []).find((job) => String(job.job_id || '') === trackedJobId);
            if (!match) {
                JobManager.clearPendingJob(trackedJobId);
                return false;
            }

            state.selectedHistoryJobId = trackedJobId;
            const strip = document.getElementById('historyStrip');
            if (strip) {
                const selected = strip.querySelectorAll('.history-item.is-selected');
                selected.forEach((el) => el.classList.remove('is-selected'));
                const tile = strip.querySelector(`.history-item[data-job-id="${trackedJobId}"]`);
                if (tile) tile.classList.add('is-selected');
            }
            await handleHistorySelect(match);
            JobManager.clearPendingJob(trackedJobId);
            return true;
        }

        if (phase === 'error' || phase === 'cancelled') {
            JobManager.clearPendingJob(trackedJobId);
        }
    } catch (e) {
        // Ignore restore failures; keep normal startup flow.
    }
    return false;
}

document.addEventListener('DOMContentLoaded', async () => {
    trackAppView();
    initFirstAppActionTracking();

    if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.requestAnimationFrame(() => {
        window.scrollTo(0, 0);
    });

    initWorkflowCollapsibles();
    attachHistoryScroll(loadHistoryPage);
    bindHistoryRetry();

    const authStartedAt = bootNowMs();
    await initAuth();
    logBootFlow('auth', 'ok', {}, bootNowMs() - authStartedAt);
    const btnLogout = document.getElementById('btnLogout');
    if (btnLogout) {
        btnLogout.addEventListener('click', logout);
    }

    const policy = await loadAndApplyPolicyConfig();
    trackRefactorRollout(policy);
    refreshTargetDropLimitHint();
    refreshLibraryDropLimitHint();
    applySliderConfig();
    applyPlanLimits(state.plan);

    initSettingsMode();
    document.addEventListener('plan:updated', (event) => {
        const nextPlan = event && event.detail ? event.detail.plan : state.plan;
        applyPlanLimits(nextPlan || state.plan);
        refreshLegacyDraftControls();
        refreshSettingsVisibility();
    });
    initHelpPopovers();
    initZoom();
    setupJobEventHandlers();
    document.addEventListener('job:complete', (event) => {
        const jobId = String(event?.detail?.jobId || state.jobId || '').trim();
        window.setTimeout(() => {
            _refreshHistoryUntilJobVisible(jobId).catch(() => {});
        }, 250);
    });
    document.addEventListener('workflow:sequence', (event) => {
        const detail = (event && event.detail) ? event.detail : {};
        if (String(detail.phase || '') !== 'stage_transition_end') return;
        if (String(detail.to_stage || '') !== 'history') return;
        _ensureHistoryStageHydrated();
    });
    Presets.init();
    initTargetHandlers();
    initLibraryUploadHandlers();
    initControlHandlers();
    initCreateDraftPersistence();
    enableHorizontalWheelScroll();
    enableProxyWheelScroll();
    enableWorkflowWheelRouting();
    enablePageEdgeFeedback();
    logBootFlow('interactive', 'ok', { nav_type: getNavigationType() }, bootNowMs() - BOOT_T0_MS);

    document.addEventListener('source:uploaded', async (e) => {
        const detail = (e && e.detail) ? e.detail : {};
        if (detail.source_photo) {
            SourcePhotos.setSelectedSourceFromUpload(detail.source_photo, {
                jobId: detail.job_id || detail.jobId || '',
                filename: detail.filename || '',
                dedupHit: Boolean(detail.dedup_hit),
            });
        }
        refreshSettingsVisibility();
    });

    refreshSettingsVisibility();
    const dataStartedAt = bootNowMs();
    const [libsLoad, historyLoad, photosLoad] = await Promise.all([
        timed(() => Lib.refreshLibraries()),
        timed(() => loadHistoryPage()),
        timed(() => SourcePhotos.loadRecentPhotos()),
    ]);

    logBootFlow('libraries', libsLoad.status === 'fulfilled' ? 'ok' : 'error', {
        count: Array.isArray(state.libraries) ? state.libraries.length : 0,
    }, libsLoad.durationMs);
    logBootFlow('history', historyLoad.status === 'fulfilled' ? 'ok' : 'error', {
        count: Array.isArray(state.history) ? state.history.length : 0,
    }, historyLoad.durationMs);
    logBootFlow('recent_photos', photosLoad.status === 'fulfilled' ? 'ok' : 'error', {
        count: Array.isArray(state.sourcePhotos) ? state.sourcePhotos.length : 0,
    }, photosLoad.durationMs);
    logBootFlow('data_all', (libsLoad.status === 'fulfilled' && historyLoad.status === 'fulfilled' && photosLoad.status === 'fulfilled') ? 'ok' : 'partial', {
        libraries: libsLoad.status === 'fulfilled' ? 'ok' : 'error',
        history: historyLoad.status === 'fulfilled' ? 'ok' : 'error',
        recent_photos: photosLoad.status === 'fulfilled' ? 'ok' : 'error',
    }, bootNowMs() - dataStartedAt);

    if (libsLoad.status === 'rejected') {
        console.warn('Initial library load failed:', libsLoad.reason);
    }
    if (historyLoad.status === 'rejected') {
        state.historyBootStatus = 'error';
        setHistorySkeletonVisible(false);
        refreshHistoryEmptyState();
        console.warn('Initial history load failed:', historyLoad.reason);
    }
    if (photosLoad.status === 'rejected') {
        console.warn('Initial recent photos load failed:', photosLoad.reason);
    }
    refreshSettingsVisibility();
    const restoredViewerSelection = await restoreViewerSelectionForCurrentStage();
    logBootFlow('restore_viewer_selection', restoredViewerSelection ? 'ok' : 'none', {}, 0);

    const handledBillingReturn = await resumeBillingCheckoutReturnIfAny();

    JobManager.resetResumeUiToIdle();
    const resumeStartedAt = bootNowMs();
    const resumed = await JobManager.resumeActiveJobIfAny();
    logBootFlow('resume_active_job', resumed ? 'resumed' : 'none', {}, bootNowMs() - resumeStartedAt);
    if (resumed && state.selectedLibraryId) {
        const libSelectStartedAt = bootNowMs();
        try {
            await Lib.handleLibSelect(state.selectedLibraryId);
            logBootFlow('resume_library_select', 'ok', { had_library: true }, bootNowMs() - libSelectStartedAt);
        } catch (e) {
            console.warn('Failed to reselect library for resumed job:', e);
            logBootFlow('resume_library_select', 'error', { had_library: true }, bootNowMs() - libSelectStartedAt);
        }
    }
    if (resumed) {
        await SourcePhotos.loadRecentPhotos();
    } else if (!handledBillingReturn) {
        const restoreStartedAt = bootNowMs();
        const restored = await restoreCompletedTrackedJobIfAny();
        logBootFlow('restore_tracked_job', restored ? 'ok' : 'none', {}, bootNowMs() - restoreStartedAt);
    }
    refreshSettingsVisibility();
    const handledDownloadGateReturn = handledBillingReturn
        ? false
        : await resumePendingDownloadGateAfterLoginIfAny();
    const handledPostLoginReturn = (!handledBillingReturn && !handledDownloadGateReturn)
        ? await restorePostLoginReturnContextIfAny()
        : false;
    if (!resumed && !handledBillingReturn && !handledDownloadGateReturn && !handledPostLoginReturn) {
        const draftRestoreStartedAt = bootNowMs();
        const restoredCreateDraft = await restoreCreateDraftState();
        logBootFlow(
            'restore_create_draft',
            restoredCreateDraft ? 'ok' : 'none',
            {},
            bootNowMs() - draftRestoreStartedAt,
        );
    }

    initGuidedOnboarding({
        historyCount: Array.isArray(state.history) ? state.history.length : 0,
        resumed,
        handledBillingReturn: Boolean(handledBillingReturn || handledDownloadGateReturn || handledPostLoginReturn),
    });
});
